import { Suspense, useState, type PointerEvent } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { Icon } from '@/components/icon'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar/sidebar'
import { useRegionLoading } from '@/contexts/region-loading'
import { ListaRegionais, ListaRegionaisSkeleton } from '@/features/regionais/components/regional-list'
import { useTrocaRegional } from '@/features/regionais/hooks'
import { useSidebar } from '@/hooks/use-sidebar'
import type { TUsuarioLogado } from '@/http'
import { showToast } from '@/infra/sonner'
import type { TUnidadesDeNegocioDisponiveisData } from '@/types/unidade-negocio'
import { getIniciaisPrimeiroUltimoNome, getPrimeiroUltimoNome } from '@/utils'

type RegionalSwitcherProps = {
  user: TUsuarioLogado
  isEmAtendimento: boolean
}

export function RegionalSwitcher({ user, isEmAtendimento = false }: Readonly<RegionalSwitcherProps>) {
  const { t } = useTranslation()
  const { isMobile } = useSidebar()
  const navigate = useNavigate()
  const { regionalAtual, handleRegionalChange } = useTrocaRegional(user)
  const { startRegionLoading } = useRegionLoading()
  const [regionalSelecionada, setRegionalSelecionada] = useState<TUnidadesDeNegocioDisponiveisData>(regionalAtual)

  const handleTrocaRegional = async () => {
    startRegionLoading()
    await handleRegionalChange(regionalSelecionada)
    const route = regionalSelecionada.localidade === 'pt-BR' ? '/' : '/es-CL'
    navigate(route, { replace: true })
  }

  const handleAbrirListaRegionais = (event: PointerEvent<HTMLButtonElement>) => {
    if (isEmAtendimento) {
      event.preventDefault()
      showToast({
        message:
          'Não é possível alterar a regional com atendimento em andamento. Encerre o atendimento e altere a regional.',
        variant: 'error',
        closeButton: true,
      })
    }
    return event
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <AlertDialog>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                onPointerDown={handleAbrirListaRegionais}
                type="button"
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <Avatar className="size-8 rounded-lg">
                  <AvatarFallback className="rounded-lg border bg-zinc-200/60">
                    {getIniciaisPrimeiroUltimoNome(user?.nome) || ''}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{getPrimeiroUltimoNome(user?.nome)}</span>
                  <span className="truncate text-xs">{user?.unidadeDeNegocio}</span>
                </div>
                <Icon name="chevron-up-down" className="ml-auto" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
              align="start"
              side={isMobile ? 'bottom' : 'right'}
              sideOffset={4}
            >
              <Suspense fallback={<ListaRegionaisSkeleton />}>
                <ListaRegionais user={user} onClick={setRegionalSelecionada} />
              </Suspense>
            </DropdownMenuContent>
          </DropdownMenu>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="text-sky-800">
                {t('layouts.sidebar.regionais.modal.titulo')}
              </AlertDialogTitle>
              <AlertDialogDescription>{t('layouts.sidebar.regionais.modal.descricao')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('layouts.sidebar.regionais.modal.cancelar')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleTrocaRegional}>
                {t('layouts.sidebar.regionais.modal.confirmar')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
